import React from 'react'
import type {
  CustomReferralWallet,
  EquityFormData,
} from 'submission/lib/equity-form-defaults'

type Props = {
  form: EquityFormData
  setForm: React.Dispatch<React.SetStateAction<EquityFormData>>
  errors: Record<string, string>
}

const emptyCustom: CustomReferralWallet = {
  walletAddress: '',
  telegram: '',
  promoCode: '',
  discountPercentage: '',
  kickbackPercentage: '',
}

const ReferralSection: React.FC<Props> = ({ form, setForm, errors }) => {
  const updateCustom = (
    idx: number,
    k: keyof CustomReferralWallet,
    v: string,
  ) =>
    setForm((p) => ({
      ...p,
      referralCustomWallets: p.referralCustomWallets.map((row, i) =>
        i === idx ? { ...row, [k]: v } : row,
      ),
    }))

  return (
    <section className="bg-white border border-slate-200 rounded p-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Referrals</h2>
      <label className="flex items-center text-sm gap-2 mb-3">
        <input
          type="checkbox"
          checked={form.referralEnabled}
          onChange={(e) =>
            setForm((p) => ({ ...p, referralEnabled: e.target.checked }))
          }
        />
        Enable referrals
      </label>

      {form.referralEnabled && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col text-sm">
              <span className="text-slate-700 mb-1">General discount (%)</span>
              <input
                type="number"
                min={0}
                step="any"
                className="border rounded px-2 py-1 text-slate-900 bg-white"
                value={form.referralGeneralDiscount}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    referralGeneralDiscount: e.target.value,
                  }))
                }
              />
            </label>
            <label className="flex flex-col text-sm">
              <span className="text-slate-700 mb-1">General kickback (%)</span>
              <input
                type="number"
                min={0}
                step="any"
                className="border rounded px-2 py-1 text-slate-900 bg-white"
                value={form.referralGeneralKickback}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    referralGeneralKickback: e.target.value,
                  }))
                }
              />
            </label>
            <label className="flex flex-col text-sm md:col-span-2">
              <span className="text-slate-700 mb-1">
                Top-level referral discount
              </span>
              <input
                type="number"
                min={0}
                step="any"
                className="border rounded px-2 py-1 text-slate-900 bg-white"
                value={form.referralDiscount}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    referralDiscount: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-medium text-slate-700">
              Custom wallets
            </h3>
            {form.referralCustomWallets.map((row, i) => (
              <div
                key={i}
                className="border rounded p-3 grid grid-cols-1 md:grid-cols-2 gap-3"
              >
                <label className="flex flex-col text-sm md:col-span-2">
                  <span className="text-slate-700 mb-1">Wallet address</span>
                  <input
                    className="border rounded px-2 py-1 text-slate-900 bg-white"
                    value={row.walletAddress}
                    onChange={(e) =>
                      updateCustom(i, 'walletAddress', e.target.value)
                    }
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="text-slate-700 mb-1">Telegram</span>
                  <input
                    className="border rounded px-2 py-1 text-slate-900 bg-white"
                    value={row.telegram}
                    onChange={(e) =>
                      updateCustom(i, 'telegram', e.target.value)
                    }
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="text-slate-700 mb-1">Promo code</span>
                  <input
                    className="border rounded px-2 py-1 text-slate-900 bg-white"
                    value={row.promoCode}
                    onChange={(e) =>
                      updateCustom(i, 'promoCode', e.target.value)
                    }
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="text-slate-700 mb-1">Discount %</span>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    className="border rounded px-2 py-1 text-slate-900 bg-white"
                    value={row.discountPercentage}
                    onChange={(e) =>
                      updateCustom(i, 'discountPercentage', e.target.value)
                    }
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="text-slate-700 mb-1">Kickback %</span>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    className="border rounded px-2 py-1 text-slate-900 bg-white"
                    value={row.kickbackPercentage}
                    onChange={(e) =>
                      updateCustom(i, 'kickbackPercentage', e.target.value)
                    }
                  />
                </label>
                <button
                  type="button"
                  className="self-start px-3 py-1 border rounded text-sm"
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      referralCustomWallets: p.referralCustomWallets.filter(
                        (_, idx) => idx !== i,
                      ),
                    }))
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="self-start px-3 py-1 border rounded text-sm"
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  referralCustomWallets: [
                    ...p.referralCustomWallets,
                    { ...emptyCustom },
                  ],
                }))
              }
            >
              Add custom wallet
            </button>
          </div>
        </div>
      )}
      {errors.referral && (
        <p className="text-red-600 text-xs mt-2">{errors.referral}</p>
      )}
    </section>
  )
}

export default ReferralSection
